import React from "react";
import { styled } from "@glitz/react";
import { useNavigate } from "react-router-dom";
import {
  FaSwimmer,
  FaBiking,
  FaRunning,
  FaBoxOpen,
  FaCalculator,
} from "react-icons/fa";
import CloseIcon from "./CloseIcon";
import {
  spacing,
  fontSize,
  fontWeight,
  black,
  white,
  green,
  lightGrey,
  grey,
} from "../theme";
import navConfig from "../nav.config";

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
};

const categoryIcons = (icon?: string) => {
  switch (icon) {
    case "bike":
      return <FaBiking />;
    case "swim":
      return <FaSwimmer />;
    case "run":
      return <FaRunning />;
    case "calculator":
      return <FaCalculator />;
    case "package":
      return <FaBoxOpen />;
    default:
      return null;
  }
};

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { categories, tools } = navConfig;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {isOpen && <Overlay onClick={onClose} />}
      <Drawer className={isOpen ? "mobile-menu-open" : "mobile-menu-closed"}>
        <Top>
          <DrawerTitle>Kategorier</DrawerTitle>
          <CloseIcon onClose={onClose} />
        </Top>
        <CategoryList>
          {categories.map((category) => (
            <CategoryItem
              key={category.slug}
              onClick={() => handleNavigate(`/kategorier/${category.slug}`)}
            >
              <IconText>
                {categoryIcons(category.icon)} {category.label}
              </IconText>
              <Arrow>›</Arrow>
            </CategoryItem>
          ))}
          {tools.map((tool) => (
            <CategoryItem
              key={tool.path}
              onClick={() => handleNavigate(tool.path)}
            >
              <IconText>
                {categoryIcons(tool.icon)} {tool.label}
              </IconText>
              <Arrow>›</Arrow>
            </CategoryItem>
          ))}
        </CategoryList>
      </Drawer>
    </>
  );
};

const Overlay = styled.div({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.4)",
  zIndex: 1199,
});

const Drawer = styled.div({
  position: "fixed",
  top: 0,
  left: 0,
  width: "300px",
  maxWidth: "80vw",
  height: "100vh",
  backgroundColor: white,
  zIndex: 1200,
  display: "flex",
  flexDirection: "column",
  boxShadow: "4px 0 20px rgba(0,0,0,0.15)",
  transitionProperty: "transform",
  transitionDuration: "0.3s",
  transitionTimingFunction: "ease-in-out",
});

const Top = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: { x: spacing.m, y: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const DrawerTitle = styled.h2({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
});

const CategoryList = styled.nav({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  flex: { grow: 1, shrink: 1, basis: 0 },
});

const CategoryItem = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: { x: spacing.l, y: spacing.m },
  cursor: "pointer",
  fontSize: fontSize.m,
  fontWeight: fontWeight.regular,
  color: black,
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const IconText = styled.span({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
});

const Arrow = styled.span({
  fontSize: fontSize.xl,
  color: green,
  fontWeight: fontWeight.bold,
});

export default MobileMenu;
